import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Plus, Trash2, Ban } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const empty = { national_id: "", name: "", reason: "" };

export default function Blacklist() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/blacklist").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/blacklist", form);
      toast.success("تم إضافة رقم الهوية إلى القائمة السوداء");
      setForm(empty); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("هل تريد إزالة هذا السجل من القائمة السوداء؟")) return;
    try { await api.delete(`/blacklist/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-10">
        <div className="mb-4">
          <Button variant="ghost" onClick={() => navigate("/settings")}
            data-testid="blacklist-back" className="text-emerald-900 hover:bg-emerald-50">
            <ArrowRight className="h-4 w-4 ml-2" /> العودة للإعدادات
          </Button>
        </div>
        <PageHeader
          title="القائمة السوداء"
          subtitle={`منع تسجيل أرقام هوية معينة في كلا البوابتين (${items.length} محظور حالياً)`}
          actions={
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="blacklist-add-btn" className="bg-emerald-900 hover:bg-emerald-800 text-white">
                  <Plus className="h-4 w-4 ml-2" /> إضافة رقم هوية
                </Button>
              </DialogTrigger>
              <DialogContent dir="rtl">
                <DialogHeader><DialogTitle className="text-right">إضافة إلى القائمة السوداء</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4">
                  <div><Label>رقم الهوية *</Label>
                    <Input data-testid="bl-form-id" value={form.national_id}
                      onChange={(e) => setForm({ ...form, national_id: e.target.value })} required />
                  </div>
                  <div><Label>الاسم (اختياري)</Label>
                    <Input data-testid="bl-form-name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div><Label>سبب الحظر (اختياري)</Label>
                    <Textarea data-testid="bl-form-reason" value={form.reason}
                      onChange={(e) => setForm({ ...form, reason: e.target.value })}
                      placeholder="مثال: تقييم سلبي من مشرف الرحلة" />
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="bl-form-save"
                      className="bg-emerald-900 hover:bg-emerald-800 text-white">إضافة</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          }
        />

        <Card className="border-emerald-900/10 overflow-x-auto">
          {items.length === 0 ? (
            <div className="p-16 text-center">
              <Ban className="h-10 w-10 text-emerald-900/30 mx-auto mb-3" />
              <p className="text-slate-500">القائمة فارغة.</p>
            </div>
          ) : (
            <table className="w-full text-right" data-testid="blacklist-table">
              <thead className="bg-emerald-950 text-white">
                <tr>
                  <th className="p-3 font-heading">رقم الهوية</th>
                  <th className="p-3 font-heading">الاسم</th>
                  <th className="p-3 font-heading">السبب</th>
                  <th className="p-3 font-heading">تاريخ الإضافة</th>
                  <th className="p-3 font-heading w-16"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((x, i) => (
                  <tr key={x.id} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="p-3 tabular-nums font-bold text-emerald-950">{x.national_id}</td>
                    <td className="p-3 text-slate-700">{x.name || "—"}</td>
                    <td className="p-3 text-slate-600 text-sm">{x.reason || "—"}</td>
                    <td className="p-3 text-slate-500 text-xs">{new Date(x.created_at).toLocaleDateString("ar-SA")}</td>
                    <td className="p-3">
                      <Button size="sm" variant="ghost" data-testid={`bl-delete-${x.id}`}
                        onClick={() => remove(x.id)} className="text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
